import { useCallback } from 'react';
/**
 * Builds a portal's `useFeatureFlag` hook on top of its `useFeatureFlagsSelector` hook.
 */
export function createUseFeatureFlag(useFeatureFlagsSelector) {
    /**
     * Subscribes to a single feature flag value.
     *
     * This is the most ergonomic and safest way for UI components to read flags.
     * The component will only rerender when the specified flag changes.
     *
     * Internally this uses `useFeatureFlagsSelector` with a minimal selector to ensure
     * fine-grained subscription behavior.
     *
     * ✅ Use this hook when:
     * - a component depends on exactly one flag
     * - or the several flags it depends on don't relate to each other, if they do relate to each other they might be combinable via `useFeatureFlagsSelector`
     *
     * Example:
     * ```ts
     * const newCheckoutEnabled = useFeatureFlag('newCheckout');
     * ```
     *
     * @param key - The specific feature flag to subscribe to.
     * @returns The current value of the requested feature flag.
     */
    return function useFeatureFlag(key) {
        return useFeatureFlagsSelector(useCallback((flags) => flags[key], [key]));
    };
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiY3JlYXRlVXNlRmVhdHVyZUZsYWcuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi9zcmMvaG9va3MvZmVhdHVyZUZsYWdzL2NyZWF0ZVVzZUZlYXR1cmVGbGFnLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBLE9BQU8sRUFBRSxXQUFXLEVBQUUsTUFBTSxPQUFPLENBQUM7QUFJcEM7O0dBRUc7QUFDSCxNQUFNLFVBQVUsb0JBQW9CLENBQVMsdUJBQXdEO0lBQ25HOzs7Ozs7Ozs7Ozs7Ozs7Ozs7OztPQW9CRztJQUNILE9BQU8sU0FBUyxjQUFjLENBQUMsR0FBaUI7UUFDOUMsT0FBTyx1QkFBdUIsQ0FBQyxXQUFXLENBQUMsQ0FBQyxLQUFhLEVBQUUsRUFBRSxDQUFDLEtBQUssQ0FBQyxHQUFHLENBQUMsRUFBRSxDQUFDLEdBQUcsQ0FBQyxDQUFDLENBQUMsQ0FBQztJQUNwRixDQUFDLENBQUM7QUFDSixDQUFDIn0=